import React from "react";

/*
 * ===========================================================================
 * CONSTANTS
 * ===========================================================================
 */

const DEFAULT_TIMEZONE = "Asia/Kolkata";

const TIMEZONES = [
  {
    value: "Asia/Kolkata",
    label: "India Standard Time (Asia/Kolkata)",
  },
  {
    value: "Asia/Dubai",
    label: "Gulf Standard Time (Asia/Dubai)",
  },
  {
    value: "Asia/Singapore",
    label: "Singapore Time (Asia/Singapore)",
  },
  {
    value: "Europe/London",
    label: "UK Time (Europe/London)",
  },
  {
    value: "Europe/Berlin",
    label: "Central European Time (Europe/Berlin)",
  },
  {
    value: "America/New_York",
    label: "Eastern Time (America/New_York)",
  },
  {
    value: "America/Chicago",
    label: "Central Time (America/Chicago)",
  },
  {
    value: "America/Denver",
    label: "Mountain Time (America/Denver)",
  },
  {
    value: "America/Los_Angeles",
    label: "Pacific Time (America/Los_Angeles)",
  },
];

const CHANNELS = [
  {
    value: "whatsapp",
    label: "WhatsApp",
    icon: "◉",
    description: "Send the reminder through WhatsApp.",
  },
  {
    value: "email",
    label: "Email",
    icon: "✉",
    description: "Send the reminder through email.",
  },
  {
    value: "sms",
    label: "SMS",
    icon: "▣",
    description:
      "SMS delivery can be enabled when provider integration is available.",
    comingSoon: true,
  },
];

const WHATSAPP_TEMPLATES = [
  {
    value: "document_request_reminder",
    label: "Document request reminder",
    description:
      "Standard reminder containing the document upload information.",
  },
  {
    value: "reminder_with_custom_message_with_button",
    label: "Custom message + upload button",
    description: "Custom WhatsApp message with a direct upload action.",
  },
  {
    value: "reminder_with_message",
    label: "Custom message",
    description: "Custom WhatsApp reminder without an upload button.",
  },
];

const EMAIL_TEMPLATES = [
  {
    value: "document_request",
    label: "Document request",
    description: "Standard document request email.",
  },
];

/*
 * ===========================================================================
 * ID / OBJECT HELPERS
 * ===========================================================================
 */

const createId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createDelivery = (platform) => {
  switch (platform) {
    case "whatsapp":
      return {
        id: createId(),
        platform: "whatsapp",
        template_key: "reminder_with_custom_message_with_button",
        custom_subject: "",
        custom_message: "",
        template_data: {},
        is_enabled: true,
      };

    case "email":
      return {
        id: createId(),
        platform: "email",
        template_key: "document_request",
        custom_subject: "",
        custom_message: "",
        template_data: {},
        is_enabled: true,
      };

    case "sms":
      return {
        id: createId(),
        platform: "sms",
        template_key: "",
        custom_subject: "",
        custom_message: "",
        template_data: {},
        is_enabled: false,
      };

    default:
      return {
        id: createId(),
        platform,
        template_key: "",
        custom_subject: "",
        custom_message: "",
        template_data: {},
        is_enabled: true,
      };
  }
};

const createReminder = (timezone = DEFAULT_TIMEZONE, reminderNumber = 1) => ({
  id: createId(),

  reminderNumber,

  date: "",
  time: "09:00",

  timezone,

  deliveries: [createDelivery("whatsapp")],
});

/*
 * ===========================================================================
 * NORMALIZATION
 * ===========================================================================
 *
 * Keeps this component backwards-compatible with older frontend state.
 */

const normalizeDelivery = (delivery) => {
  const platform = delivery?.platform || "whatsapp";

  return {
    ...createDelivery(platform),
    ...delivery,

    id: delivery?.id || createId(),

    platform,

    template_data:
      delivery?.template_data && typeof delivery.template_data === "object"
        ? delivery.template_data
        : {},

    is_enabled: delivery?.is_enabled !== false,
  };
};

const normalizeReminder = (
  reminder,
  index,
  fallbackTimezone = DEFAULT_TIMEZONE,
) => {
  let deliveries = [];

  if (Array.isArray(reminder?.deliveries)) {
    deliveries = reminder.deliveries.map(normalizeDelivery);
  } else if (Array.isArray(reminder?.platforms)) {
    deliveries = reminder.platforms.map((platform) => ({
      ...createDelivery(platform),

      custom_message:
        reminder.messageMode === "custom" ? reminder.message || "" : "",
    }));
  }

  return {
    ...reminder,

    id: reminder?.id || createId(),

    reminderNumber: Number(reminder?.reminderNumber) || index + 1,

    date: reminder?.date || "",

    time: reminder?.time || "09:00",

    timezone: reminder?.timezone || fallbackTimezone || DEFAULT_TIMEZONE,

    deliveries,
  };
};

/*
 * ===========================================================================
 * MAIN COMPONENT
 * ===========================================================================
 */

const AdvanceReminderOptions = ({ values, setValues }) => {
  const timezone = values?.timezone || DEFAULT_TIMEZONE;

  const reminders = Array.isArray(values?.reminders)
    ? values.reminders.map((reminder, index) =>
        normalizeReminder(reminder, index, timezone),
      )
    : [];

  /*
   * -------------------------------------------------------------------------
   * Generic state update
   * -------------------------------------------------------------------------
   */

  const updateValues = (updates) => {
    setValues((previous) => ({
      ...previous,
      ...updates,
    }));
  };

  /*
   * -------------------------------------------------------------------------
   * Global automation settings
   * -------------------------------------------------------------------------
   */

  const handleGlobalChange = (name, value) => {
    updateValues({
      [name]: value,
    });
  };

  const handleTimezoneChange = (nextTimezone) => {
    updateValues({
      timezone: nextTimezone,
    });
  };

  /*
   * -------------------------------------------------------------------------
   * Reminder operations
   * -------------------------------------------------------------------------
   */

  const addReminder = () => {
    const nextReminderNumber = reminders.length + 1;

    const newReminder = createReminder(timezone, nextReminderNumber);

    updateValues({
      reminders: [...reminders, newReminder],
    });
  };

  const removeReminder = (reminderId) => {
    const remaining = reminders.filter(
      (reminder) => reminder.id !== reminderId,
    );

    updateValues({
      reminders: remaining.map((reminder, index) => ({
        ...reminder,
        reminderNumber: index + 1,
      })),
    });
  };

  const updateReminder = (reminderId, updates) => {
    updateValues({
      reminders: reminders.map((reminder) =>
        reminder.id === reminderId
          ? {
              ...reminder,
              ...updates,
            }
          : reminder,
      ),
    });
  };

  /*
   * -------------------------------------------------------------------------
   * Delivery operations
   * -------------------------------------------------------------------------
   */

  const addDelivery = (reminderId, platform) => {
    const reminder = reminders.find((item) => item.id === reminderId);

    if (!reminder) {
      return;
    }

    const existingDeliveries = Array.isArray(reminder.deliveries)
      ? reminder.deliveries
      : [];

    /*
     * A reminder should contain one configuration
     * per delivery platform.
     */
    if (existingDeliveries.some((delivery) => delivery.platform === platform)) {
      return;
    }

    /*
     * SMS is intentionally disabled until
     * provider/backend support is enabled.
     */
    const channel = CHANNELS.find((item) => item.value === platform);

    if (channel?.comingSoon) {
      return;
    }

    updateReminder(reminderId, {
      deliveries: [...existingDeliveries, createDelivery(platform)],
    });
  };

  const removeDelivery = (reminderId, deliveryId) => {
    const reminder = reminders.find((item) => item.id === reminderId);

    if (!reminder) {
      return;
    }

    const deliveries = reminder.deliveries.filter(
      (delivery) => delivery.id !== deliveryId,
    );

    updateReminder(reminderId, {
      deliveries,
    });
  };

  const updateDelivery = (reminderId, deliveryId, updates) => {
    const reminder = reminders.find((item) => item.id === reminderId);

    if (!reminder) {
      return;
    }

    const deliveries = reminder.deliveries.map((delivery) =>
      delivery.id === deliveryId
        ? {
            ...delivery,
            ...updates,
          }
        : delivery,
    );

    updateReminder(reminderId, {
      deliveries,
    });
  };

  /*
   * -------------------------------------------------------------------------
   * Template data
   * -------------------------------------------------------------------------
   */

  const updateTemplateData = (reminderId, deliveryId, key, value) => {
    const reminder = reminders.find((item) => item.id === reminderId);

    if (!reminder) {
      return;
    }

    const delivery = reminder.deliveries.find((item) => item.id === deliveryId);

    if (!delivery) {
      return;
    }

    updateDelivery(reminderId, deliveryId, {
      template_data: {
        ...(delivery.template_data || {}),
        [key]: value,
      },
    });
  };

  /*
   * -------------------------------------------------------------------------
   * Channel helpers
   * -------------------------------------------------------------------------
   */

  const hasChannel = (reminder, platform) =>
    Array.isArray(reminder.deliveries) &&
    reminder.deliveries.some((delivery) => delivery.platform === platform);

  const getAvailableChannels = (reminder) =>
    CHANNELS.filter((channel) => !hasChannel(reminder, channel.value));

  /*
   * -------------------------------------------------------------------------
   * Template helper
   * -------------------------------------------------------------------------
   */

  const getTemplateDescription = (templates, templateKey) => {
    return (
      templates.find((template) => template.value === templateKey)
        ?.description || ""
    );
  };

  /*
   * =========================================================================
   * RENDER
   * =========================================================================
   */

  return (
    <div className="advanced-reminder-options">
      {/* ===================================================================
          GLOBAL AUTOMATION SETTINGS
          =================================================================== */}

      <div className="automation-settings">
        {/* Skip weekends */}

        <div className="automation-setting">
          <div className="automation-setting__content">
            <strong>Skip weekends</strong>

            <span>
              Move reminders scheduled for Saturday or Sunday to the next
              working day.
            </span>
          </div>

          <label className="toggle">
            <input
              type="checkbox"
              checked={values?.skipWeekends !== false}
              onChange={(event) =>
                handleGlobalChange("skipWeekends", event.target.checked)
              }
            />

            <span className="toggle__slider" />
          </label>
        </div>

        {/* Stop after submission */}

        <div className="automation-setting">
          <div className="automation-setting__content">
            <strong>Stop reminders after submission</strong>

            <span>
              Automatically stop pending reminders after the client submits the
              requested document.
            </span>
          </div>

          <label className="toggle">
            <input
              type="checkbox"
              checked={values?.stopAfterSubmission !== false}
              onChange={(event) =>
                handleGlobalChange("stopAfterSubmission", event.target.checked)
              }
            />

            <span className="toggle__slider" />
          </label>
        </div>

        {/* Request expiry */}

        <div className="automation-setting automation-setting--expiry">
          <div className="automation-setting__content">
            <strong>Request expiry</strong>

            <span>
              Automatically expire the request after the selected number of
              days.
            </span>
          </div>

          <div className="expiry-input">
            <input
              type="number"
              min="1"
              max="3650"
              value={values?.requestExpiryDays ?? 30}
              onChange={(event) =>
                handleGlobalChange("requestExpiryDays", event.target.value)
              }
            />

            <span>days</span>
          </div>
        </div>

        {/* Default timezone */}

        <div className="automation-setting automation-setting--timezone">
          <div className="automation-setting__content">
            <strong>Default timezone</strong>

            <span>
              Used when a new reminder is created. Each reminder can override
              this timezone.
            </span>
          </div>

          <select
            value={timezone}
            onChange={(event) => handleTimezoneChange(event.target.value)}
          >
            {TIMEZONES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ===================================================================
          REMINDER BUILDER HEADER
          =================================================================== */}

      <div className="reminder-builder-header">
        <div>
          <h4>Reminder schedule</h4>

          <p>
            Create as many reminders as required. Each reminder can have its own
            date, time, timezone and delivery configuration.
          </p>
        </div>

        <button
          type="button"
          className="add-reminder-btn"
          onClick={addReminder}
        >
          + Add reminder
        </button>
      </div>

      {/* ===================================================================
          EMPTY STATE
          =================================================================== */}

      {reminders.length === 0 && (
        <div className="reminder-empty-state">
          <div className="reminder-empty-state__icon">⏰</div>

          <h4>No reminders configured</h4>

          <p>
            This document request will not send automatic reminders until you
            add one.
          </p>

          <button
            type="button"
            className="add-reminder-btn"
            onClick={addReminder}
          >
            + Add first reminder
          </button>
        </div>
      )}

      {/* ===================================================================
          REMINDER LIST
          =================================================================== */}

      <div className="reminder-list">
        {reminders.map((reminder, reminderIndex) => (
          <div className="reminder-card" key={reminder.id}>
            {/* -----------------------------------------------------------
                  REMINDER HEADER
                  ----------------------------------------------------------- */}

            <div className="reminder-card__header">
              <div className="reminder-card__title">
                <span className="reminder-number">{reminderIndex + 1}</span>

                <div>
                  <h5>Reminder {reminderIndex + 1}</h5>

                  <span>Automated client notification</span>
                </div>
              </div>

              <button
                type="button"
                className="remove-reminder-btn"
                onClick={() => removeReminder(reminder.id)}
              >
                Remove
              </button>
            </div>

            {/* -----------------------------------------------------------
                  SCHEDULE
                  ----------------------------------------------------------- */}

            <div className="reminder-card__section">
              <div className="reminder-card__section-title">
                <span>Schedule</span>

                <small>
                  Choose exactly when this reminder should be processed.
                </small>
              </div>

              <div className="reminder-schedule-grid">
                {/* Date */}

                <div className="reminder-field">
                  <label>Date</label>

                  <input
                    type="date"
                    value={reminder.date || ""}
                    onChange={(event) =>
                      updateReminder(reminder.id, {
                        date: event.target.value,
                      })
                    }
                  />
                </div>

                {/* Time */}

                <div className="reminder-field">
                  <label>Time</label>

                  <input
                    type="time"
                    value={reminder.time || "09:00"}
                    onChange={(event) =>
                      updateReminder(reminder.id, {
                        time: event.target.value,
                      })
                    }
                  />
                </div>

                {/* Timezone */}

                <div className="reminder-field reminder-field--timezone">
                  <label>Timezone</label>

                  <select
                    value={reminder.timezone || timezone}
                    onChange={(event) =>
                      updateReminder(reminder.id, {
                        timezone: event.target.value,
                      })
                    }
                  >
                    {TIMEZONES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* -----------------------------------------------------------
                  DELIVERY CHANNELS
                  ----------------------------------------------------------- */}

            <div className="reminder-card__section">
              <div className="reminder-card__section-title">
                <div>
                  <span>Delivery channels</span>

                  <small>
                    Configure how this particular reminder should reach the
                    client.
                  </small>
                </div>

                <div className="channel-add-menu">
                  {getAvailableChannels(reminder).map((channel) => (
                    <button
                      key={channel.value}
                      type="button"
                      className="channel-add-btn"
                      disabled={channel.comingSoon}
                      title={
                        channel.comingSoon
                          ? "Coming soon"
                          : `Add ${channel.label}`
                      }
                      onClick={() => addDelivery(reminder.id, channel.value)}
                    >
                      + {channel.label}
                      {channel.comingSoon && <span> (Soon)</span>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="delivery-list">
                {reminder.deliveries?.length === 0 && (
                  <div className="delivery-empty">
                    Add at least one delivery channel for this reminder.
                  </div>
                )}

                {reminder.deliveries?.map((delivery) => {
                  const channel = CHANNELS.find(
                    (item) => item.value === delivery.platform,
                  );

                  return (
                    <div
                      className={`delivery-card ${
                        delivery.is_enabled === false
                          ? "delivery-card--disabled"
                          : ""
                      }`}
                      key={delivery.id}
                    >
                      {/* ------------------------------------------------
                              DELIVERY HEADER
                              ------------------------------------------------ */}

                      <div className="delivery-card__header">
                        <div className="delivery-card__identity">
                          <span className="delivery-card__icon">
                            {channel?.icon || "•"}
                          </span>

                          <div>
                            <strong>
                              {channel?.label || delivery.platform}
                            </strong>

                            <span>{channel?.description}</span>
                          </div>
                        </div>

                        <div className="delivery-card__actions">
                          <label className="toggle toggle--small">
                            <input
                              type="checkbox"
                              checked={delivery.is_enabled !== false}
                              onChange={(event) =>
                                updateDelivery(reminder.id, delivery.id, {
                                  is_enabled: event.target.checked,
                                })
                              }
                            />

                            <span className="toggle__slider" />
                          </label>

                          <button
                            type="button"
                            className="remove-delivery-btn"
                            onClick={() =>
                              removeDelivery(reminder.id, delivery.id)
                            }
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* ------------------------------------------------
                              DISABLED NOTICE
                              ------------------------------------------------ */}

                      {delivery.is_enabled === false && (
                        <div className="delivery-disabled-message">
                          This delivery is disabled and will not be sent.
                        </div>
                      )}

                      {/* =================================================
                              WHATSAPP
                              ================================================= */}

                      {delivery.platform === "whatsapp" && (
                        <div className="delivery-card__body">
                          <div className="delivery-field">
                            <label>WhatsApp template</label>

                            <select
                              value={delivery.template_key || ""}
                              onChange={(event) =>
                                updateDelivery(reminder.id, delivery.id, {
                                  template_key: event.target.value,
                                })
                              }
                            >
                              <option value="">Select template</option>

                              {WHATSAPP_TEMPLATES.map((template) => (
                                <option
                                  key={template.value}
                                  value={template.value}
                                >
                                  {template.label}
                                </option>
                              ))}
                            </select>

                            {delivery.template_key && (
                              <small>
                                {getTemplateDescription(
                                  WHATSAPP_TEMPLATES,
                                  delivery.template_key,
                                )}
                              </small>
                            )}
                          </div>

                          <div className="delivery-field">
                            <label>Custom message</label>

                            <textarea
                              rows={4}
                              value={delivery.custom_message || ""}
                              placeholder="Write a personalized WhatsApp reminder..."
                              onChange={(event) =>
                                updateDelivery(reminder.id, delivery.id, {
                                  custom_message: event.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="template-variable-help">
                            <strong>Available variables</strong>

                            <div>
                              <code>{"{client_name}"}</code>

                              <code>{"{ca_name}"}</code>

                              <code>{"{document_title}"}</code>

                              <code>{"{due_date}"}</code>

                              <code>{"{upload_link}"}</code>
                            </div>
                          </div>

                          {/* Template data remains available
                                  for future dynamic configuration. */}

                          {delivery.template_key ===
                            "reminder_with_custom_message_with_button" && (
                            <div className="delivery-field">
                              <label>Upload button label</label>

                              <input
                                type="text"
                                value={
                                  delivery.template_data?.upload_button_label ||
                                  ""
                                }
                                placeholder="Upload document"
                                onChange={(event) =>
                                  updateTemplateData(
                                    reminder.id,
                                    delivery.id,
                                    "upload_button_label",
                                    event.target.value,
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* =================================================
                              EMAIL
                              ================================================= */}

                      {delivery.platform === "email" && (
                        <div className="delivery-card__body">
                          <div className="delivery-field">
                            <label>Email template</label>

                            <select
                              value={delivery.template_key || ""}
                              onChange={(event) =>
                                updateDelivery(reminder.id, delivery.id, {
                                  template_key: event.target.value,
                                })
                              }
                            >
                              <option value="">Select template</option>

                              {EMAIL_TEMPLATES.map((template) => (
                                <option
                                  key={template.value}
                                  value={template.value}
                                >
                                  {template.label}
                                </option>
                              ))}
                            </select>

                            {delivery.template_key && (
                              <small>
                                {getTemplateDescription(
                                  EMAIL_TEMPLATES,
                                  delivery.template_key,
                                )}
                              </small>
                            )}
                          </div>

                          <div className="delivery-field">
                            <label>Custom subject</label>

                            <input
                              type="text"
                              value={delivery.custom_subject || ""}
                              placeholder="e.g. Reminder: documents required"
                              onChange={(event) =>
                                updateDelivery(reminder.id, delivery.id, {
                                  custom_subject: event.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="delivery-field">
                            <label>Custom message</label>

                            <textarea
                              rows={5}
                              value={delivery.custom_message || ""}
                              placeholder="Write a personalized email message..."
                              onChange={(event) =>
                                updateDelivery(reminder.id, delivery.id, {
                                  custom_message: event.target.value,
                                })
                              }
                            />
                          </div>

                          <div className="template-variable-help">
                            <strong>Available variables</strong>

                            <div>
                              <code>{"{client_name}"}</code>

                              <code>{"{ca_name}"}</code>

                              <code>{"{document_title}"}</code>

                              <code>{"{due_date}"}</code>

                              <code>{"{upload_link}"}</code>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* =================================================
                              SMS
                              ================================================= */}

                      {delivery.platform === "sms" && (
                        <div className="delivery-card__body">
                          <div className="coming-soon-panel">
                            <strong>SMS delivery is coming soon</strong>

                            <span>
                              The reminder structure supports SMS, but the
                              provider integration is not enabled yet.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ===================================================================
          ADD ANOTHER REMINDER
          =================================================================== */}

      {reminders.length > 0 && (
        <button
          type="button"
          className="add-reminder-btn add-reminder-btn--full"
          onClick={addReminder}
        >
          + Add another reminder
        </button>
      )}

      {/* ===================================================================
          INFORMATION
          =================================================================== */}

      <div className="reminder-builder-info">
        <span className="reminder-builder-info__icon">💡</span>

        <div>
          <strong>How automated reminders work</strong>

          <p>
            Every reminder is stored as its own schedule. Each schedule can have
            one or more enabled delivery channels. The backend processes due
            schedules automatically, while request-level settings control
            weekend handling, expiry and whether reminders stop after document
            submission.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdvanceReminderOptions;
